import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Space,
  Switch,
  type TableColumnsType,
  type TablePaginationConfig,
  theme,
  Typography,
} from 'antd';
import type { FilterValue } from 'antd/es/table/interface';
import { StatusTag } from 'pos-client/components/generic/status-tag/StatusTag.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import { PosInputSearch } from 'pos-client/components/shared/Form/Search/PosSearchInput.tsx';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import { useFilters } from 'pos-client/hooks/useFilters.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { GREEN, LIGHT_GREY } from 'pos-client/utils/colours.ts';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { extractSingleFilterSelection } from 'pos-client/utils/dataTableHelpers.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type JobTypeFindFERequestModel,
  type JobTypeModel,
} from 'pos-shared/models/jobTypeSchema.ts';

export const SearchJobTypes = ({
  handleEditJobType,
}: {
  handleEditJobType: (JobType: JobTypeModel) => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const trpcContext = trpc.useUtils();
  const { showSuccess } = useNotifications();
  const { hasPermissions } = usePermissions();

  const canAddJobType = hasPermissions('add.edit.job.types');

  const statusOptions = [
    { label: t('statuses.active'), value: true },
    { label: t('statuses.inactive'), value: false },
  ];

  const { formFilters, handleFilterChange, onPageChange } =
    useFilters<JobTypeFindFERequestModel>({
      pageNumber: 1,
      resultsPerPage: 10,
      searchText: '',
      isActive: null,
    });

  const { data, isLoading: isLoadingSearch } = trpc.findJobTypes.useQuery({
    ...formFilters,
  });

  const { mutate: updateJobType, isLoading: isUpdatingJobType } =
    trpc.updateJobType.useMutation({
      onSuccess: async () => {
        showSuccess(
          t(`common.notification.success.message`, {
            action: 'updated',
            entity: 'job type',
          })
        );
        await trpcContext.findJobTypes.invalidate();
      },
    });

  const tableData = useTableData<JobTypeModel>(data?.results, (x) => x.id);

  const resetPaging = () => {
    onPageChange(1);
  };

  const onSearchJobType = (value: string) => {
    handleFilterChange({
      filter: 'searchText',
      value,
    });

    resetPaging();
  };

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>
    ) => {
      handleFilterChange({
        filter: 'isActive',
        value: extractSingleFilterSelection(filters, 'isActive'),
      });
    },
    [handleFilterChange]
  );

  const columns: TableColumnsType<JobTypeModel> = [
    {
      title: t('jobTypes.jobType'),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => <Typography.Text>{record.name}</Typography.Text>,
    },
    {
      title: t('jobTypes.serviceType'),
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (_, record) => (
        <StatusTag
          style={{ width: '55px', textAlign: 'center' }}
          status={record.serviceType}
        />
      ),
    },
    {
      title: t('common.status.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_, record) =>
        !canAddJobType ? (
          <Space
            size={0}
            style={{
              backgroundColor: record.isActive ? GREEN.color : LIGHT_GREY,
              color: token.colorWhite,
              borderRadius: '50px',
              paddingLeft: token.paddingXS,
              paddingRight: token.paddingXS,
              fontSize: token.fontSizeSM,
              height: '22px',
            }}
          >
            {record.isActive === true ? (
              <>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={token.colorWhite}
                  style={{
                    marginRight: token.marginXXS,
                  }}
                />
                {t('statuses.active')}
              </>
            ) : (
              t('statuses.inactive')
            )}
          </Space>
        ) : (
          <Switch
            style={{
              backgroundColor: record.isActive ? GREEN.color : LIGHT_GREY,
            }}
            checkedChildren={
              <>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={token.colorWhite}
                  style={{
                    marginRight: token.marginXXS,
                  }}
                />
                {t('statuses.active')}
              </>
            }
            checked={record.isActive === true}
            unCheckedChildren={t('statuses.inactive')}
            aria-label={t('common.status.status')}
            defaultChecked={record.isActive === true}
            disabled={isUpdatingJobType}
            onChange={() =>
              updateJobType({
                ...record,
                isActive: record.isActive === true ? false : true,
              })
            }
          />
        ),
    },
    {
      title: '',
      key: 'id',
      align: 'center',
      width: 80,
      hidden: !canAddJobType,
      render: (_, record) => (
        <Space size="middle">
          <Button
            size="small"
            onClick={() => handleEditJobType(record)}
            disabled={isUpdatingJobType}
          >
            {t('common.buttons.edit')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PosGrid filters>
        <PosColumn span={24} lg={12} xl={8}>
          <PosInputSearch
            onSearch={onSearchJobType}
            defaultValue={formFilters.searchText}
            placeholder={t('common.placeholders.enterField', {
              field: t('jobTypes.jobType'),
            })}
            disabled={isLoadingSearch}
            loading={isLoadingSearch}
            size="large"
            allowClear
          />
        </PosColumn>
        <PosColumn span={24} lg={12} xl={8}>
          <PosSelect
            placeholder={t('common.status.selectStatus')}
            options={statusOptions}
            defaultValue={formFilters.isActive}
            onSelect={(value) => {
              handleFilterChange({
                filter: 'isActive',
                value,
              });

              resetPaging();
            }}
            onClear={() =>
              handleFilterChange({ filter: 'isActive', value: undefined })
            }
            allowClear
            size="large"
            style={fullWidth}
          />
        </PosColumn>
      </PosGrid>
      <PosTable
        id="job-type-search-results"
        dataSource={tableData}
        columns={columns}
        isLoading={isLoadingSearch}
        onChange={handleTableChange}
        padding={24}
        paging={{
          current: data?.pageNumber || 0,
          pageSize: formFilters.resultsPerPage,
          total: data?.totalResults || 0,
          onChange: onPageChange,
        }}
      />
    </>
  );
};
